<template>
  <v-layout row wrap>
    <v-flex xs12>
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        :nudge-right="0"
        lazy
        transition="scale-transition"
        offset-y
        full-width
        max-width="290px"
        min-width="290px"
      >
        <template v-slot:activator="{ on }">
          <ValidationProvider :name="$attrs.label" :immediate="true" :rules="rules">
            <v-text-field
              v-model="dateFormatted"
              slot-scope="{ errors, valid }"
              persistent-hint
              clearable
              :label="$attrs.label"
              v-bind="$attrs"
              :success="valid && success"
              :error="error"
              :error-messages="errors"
              :hint="getDateWarningMessage()"
              :class="{ 'date-warning': hasDateWarning() }"
              type="text"
              @blur="handleDateInput"
              @input="handleManualInput"
              v-on="on"
            />
          </ValidationProvider>
        </template>
        <v-date-picker v-model="date" no-title @input="handleDatePickerInput" />
      </v-menu>
    </v-flex>
  </v-layout>
</template>

<script>
import { ValidationProvider } from 'vee-validate';

export default {
  name: 'DateInput',
  components: {
    ValidationProvider,
  },
  props: {
    value: { type: String, default: null },
    items: { type: Array, default: () => [] },
    success: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    rules: { type: [Object, String], default: 'required' },
  },
  data: () => ({
    date: null,
    dateFormatted: null,
    menu: false,
  }),
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue !== this.dateFormatted) {
          this.dateFormatted = newValue;
          this.date = this.parseDate(newValue);
        }
      },
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${day}.${month}.${year}`;
    },
    parseDate(date) {
      if (!date) return null;

      // Handle different date formats
      let day, month, year;

      // Try dd.mm.yyyy format
      const dotMatch = date.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (dotMatch) {
        [, day, month, year] = dotMatch;
      } else {
        // Try yyyy-mm-dd format
        const dashMatch = date.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (dashMatch) {
          [, year, month, day] = dashMatch;
        } else {
          return null;
        }
      }

      // Validate date parts
      day = parseInt(day, 10);
      month = parseInt(month, 10);
      year = parseInt(year, 10);

      // Basic date validation
      if (month < 1 || month > 12) return null;
      if (day < 1 || day > 31) return null;
      if (year < 1900 || year > 2100) return null;

      // Create date object to validate the date
      const dateObj = new Date(year, month - 1, day);
      if (dateObj.getMonth() !== month - 1) return null; // Invalid date like 31.04.2024

      return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    },
    handleDatePickerInput(newDate) {
      this.menu = false;
      this.date = newDate;
      const formatted = this.formatDate(newDate);
      this.dateFormatted = formatted;
      this.$emit('update', formatted);
      this.$emit('input', formatted);
    },
    handleDateInput() {
      const parsedDate = this.parseDate(this.dateFormatted);
      if (parsedDate) {
        this.date = parsedDate;
        const formatted = this.formatDate(parsedDate);
        this.dateFormatted = formatted;
        this.$emit('update', formatted);
        this.$emit('input', formatted);
      }
    },
    handleManualInput(value) {
      // Immediately emit the value for v-model binding
      this.$emit('input', value);

      // Try to parse and format if it's a valid date
      const parsedDate = this.parseDate(value);
      if (parsedDate) {
        this.date = parsedDate;
      }
    },
    hasDateWarning() {
      const hasRule = typeof this.rules === 'string' && this.rules.includes('date_warning');

      if (!hasRule || !this.dateFormatted) {
        return false;
      }

      try {
        const [day, month, year] = this.dateFormatted.split('.');
        if (!day || !month || !year) {
          return false;
        }

        const inputDate = new Date(year, month - 1, day);
        const now = new Date();
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(now.getMonth() - 3);

        return inputDate < threeMonthsAgo && inputDate.getFullYear() < now.getFullYear();
      } catch (e) {
        return false;
      }
    },
    getDateWarningMessage() {
      if (this.hasDateWarning()) {
        return 'Datum je starejši od 3 mesecev in ni v tekočem letu';
      }
      return '';
    },
  },
};
</script>

<style scoped>
.date-warning >>> .v-messages__message {
  color: #fb8c00 !important;
}
.date-warning >>> .v-text-field__details {
  color: #fb8c00 !important;
}
</style>
